import type {
    DataProvider,
    GetListParams,
    GetManyReferenceParams,
} from 'react-admin';
import { createRestProvider } from './factories/createRestProvider';
import { missionProvider } from './resources/missionProvider';
import { organizationProvider } from './resources/organizationProvider';
import { resourceProvider } from './resources/resourceProvider';
import { ragResourceProvider } from './resources/ragResourceProvider';
import { missionStudyAnswerProvider } from './resources/missionStudyAnswerProvider';
import { memberNoteProvider } from './resources/memberNoteProvider';
import { missionNoteProvider } from './resources/missionNoteProvider';

const defaultMethods: (keyof DataProvider)[] = [
    'getList',
    'getManyReference',
    'getOne',
    'getMany',
    'create',
    'update',
    'updateMany',
];

const providers: Record<string, Partial<DataProvider>> = {
    activityLog: createRestProvider({
        path: 'activity-log',
        methods: ['getList'],
    }),
    animation: createRestProvider({
        path: 'animations',
        listExtraParams: ['antennaId'],
        methods: defaultMethods,
    }),
    antenna: createRestProvider({ path: 'antennas', methods: defaultMethods }),
    studyForm: createRestProvider({
        path: 'study-form',
        methods: defaultMethods,
    }),
    expensesClaim: createRestProvider({
        path: 'expenses-claims',
        methods: defaultMethods,
    }),
    formation: createRestProvider({
        path: 'formations',
        methods: defaultMethods,
    }),
    highlightedResource: createRestProvider({
        path: 'highlighted-resources',
        methods: defaultMethods,
    }),
    member: createRestProvider({ path: 'members', methods: defaultMethods }),
    memberMedia: createRestProvider({
        path: 'medias',
        methods: ['getList', 'getManyReference'],
    }),
    memberNote: memberNoteProvider,
    mission: missionProvider,
    missionStudyAnswer: missionStudyAnswerProvider,
    missionMedia: createRestProvider({
        path: 'medias',
        methods: ['getList', 'getManyReference'],
    }),
    missionNote: missionNoteProvider,
    missionType: createRestProvider({
        path: 'mission-types',
        isPaginated: false,
        methods: defaultMethods,
    }),
    organization: organizationProvider,
    ragResource: ragResourceProvider,
    resource: resourceProvider,
    supply: createRestProvider({
        path: 'supplies',
        listExtraParams: ['antennaId'],
        methods: defaultMethods,
    }),
    tags: createRestProvider({
        path: 'tags',
        methods: defaultMethods,
    }),
    task: createRestProvider({
        path: 'tasks',
        methods: [...defaultMethods, 'delete', 'deleteMany'],
    }),
};

const impl: Partial<DataProvider> = {
    getList: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.getList) {
            return provider.getList(resource, params as GetListParams);
        }
        throw new Error(`Not implemented: getList for resource "${resource}"`);
    },

    getOne: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.getOne) {
            return provider.getOne(resource, params);
        }
        throw new Error(`Not implemented: getOne for resource "${resource}"`);
    },

    getMany: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.getMany) {
            return provider.getMany(resource, params);
        }
        throw new Error(`Not implemented: getMany for resource "${resource}"`);
    },

    getManyReference: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.getManyReference) {
            return provider.getManyReference(
                resource,
                params as GetManyReferenceParams
            );
        }
        throw new Error(
            `Not implemented: getManyReference for resource "${resource}"`
        );
    },

    create: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.create) {
            return provider.create(resource, params);
        }
        throw new Error(`Not implemented: create for resource "${resource}"`);
    },

    update: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.update) {
            return provider.update(resource, params);
        }
        throw new Error(`Not implemented: update for resource "${resource}"`);
    },

    updateMany: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.updateMany) {
            return provider.updateMany(resource, params);
        }
        throw new Error(
            `Not implemented: updateMany for resource "${resource}"`
        );
    },

    delete: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.delete) {
            return provider.delete(resource, params);
        }
        throw new Error(`Not implemented: delete for resource "${resource}"`);
    },

    deleteMany: async (resource, params) => {
        const provider = providers[resource];
        if (provider?.deleteMany) {
            return provider.deleteMany(resource, params);
        }
        throw new Error(
            `Not implemented: deleteMany for resource "${resource}"`
        );
    },
};

export const apiDataProvider = impl as DataProvider;
